import { Flex, Segmented, Typography } from 'antd';
import { NotificationDrawer, NotificationList } from 'entities/notification';
import { EventLevelType, Notification } from 'entities/notification/model/types';
import { startCase } from 'lodash';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { setActivity } from 'shared/config/store/reducers/appSlice';
import { getUserMeta } from 'shared/config/store/reducers/userSlice';
import { AppDispatch } from 'shared/config/store/store';
import { useMessage } from 'shared/libs/hooks/useMessage/useMessage';
import { GridContainer } from 'shared/ui';
import { useGetNotificationsQuery, useSetLevelMutation, useDeleteNotificationMutation } from '../api';
import classes from './NotificationsPage.module.css';

const NotificationsPage = () => {
  const { formatMessage: t } = useIntl();
  const dispatch = useDispatch<AppDispatch>();
  const userMeta = useSelector(getUserMeta);
  const [notificationDrawer, setNotificationDrawer] = useState<Notification | null>(null);
  const [filter, setFilter] = useState<EventLevelType>(userMeta.notificationLevel || EventLevelType.All);
  const { showError } = useMessage();
  const { data: notifications, isLoading, isError, error } = useGetNotificationsQuery({ eventType: filter || EventLevelType.All }, { refetchOnMountOrArgChange: true });
  const [setLevelMutation] = useSetLevelMutation();
  const [deleteNotificationMutation] = useDeleteNotificationMutation();

  const availableFilters = [
    {
      label: startCase(EventLevelType.All.toLowerCase()),
      value: EventLevelType.All
    },
    {
      label: startCase(EventLevelType.Finance.toLocaleLowerCase()),
      value: EventLevelType.Finance
    },
    {
      label: startCase(EventLevelType.Text.toLocaleLowerCase()),
      value: EventLevelType.Text
    }
  ];

  const handleFilterChange = (newFilter: EventLevelType) => {
    dispatch(setActivity(true));

    setLevelMutation(newFilter)
      .unwrap()
      .then(() => {
        setFilter(newFilter);

        if (isError) {
          console.log(error);
          showError?.(t({ id: 'common.message.error' }));
        }
      })
      .catch((err) => {
        console.log(err);
        showError?.(t({ id: 'common.message.error' }));
      })
      .finally(() => {
        dispatch(setActivity(false));
      });
  };

  const onClose = () => {
    setNotificationDrawer(null);
  }

  const handleDeleteNotification = async (notificationId: number) => {
    try {
      dispatch(setActivity(true));
      await deleteNotificationMutation({ notificationId }).unwrap();
    } catch (error) {
      console.error('Failed to delete notification:', error);
      showError?.(t({ id: 'common.message.error' }));
    } finally {
      dispatch(setActivity(false));
    }
  };

  return (
    <>
      <NotificationDrawer
        open={!!notificationDrawer}
        onClose={onClose}
        notification={notificationDrawer}
      />
      <GridContainer>
        <Flex vertical align='center' gap={12} style={{ marginTop: 30 }}>
          <Typography.Title level={2}>{t({ id: 'components.notificatonspopup.components.notificationcontent.title' })}</Typography.Title>
          <Segmented
            size='small'
            options={availableFilters}
            onChange={(value) => handleFilterChange(value as EventLevelType)}
            className={classes.segmented}
            defaultValue={userMeta.notificationLevel}
          />
          <NotificationList 
            notifications={notifications?.response.notifications} 
            count={notifications?.response.count} 
            isLoading={isLoading} 
            setNotificationDrawer={setNotificationDrawer}
            onDeleteNotification={handleDeleteNotification}
          />
        </Flex>
      </GridContainer>
    </>
  )
}

export default NotificationsPage;